import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Content,
  Header,
  Heading,
  Main,
  SubHeading,
} from "../commons/Template/Template";
import { Form } from "../commons/Form/Form";
import routes from "../../routes/routes";
import { useAuth } from "../../auth/AuthContext";
import { mapAuthErrors } from "../../api/authentication";

// Log-in page
export default function Login() {
  const { t } = useTranslation(["common", "account"]);
  document.title = t("account:login.title");
  const [status, setStatus] = useState("active");

  switch (status) {
    case "active":
      return <Active onStatusChange={setStatus} />;
    case "inactive":
      return <Inactive />;
    case "unverified":
      return <Unverified />;
    default:
      return <></>;
  }
}

function Active({ onStatusChange }) {
  const { t } = useTranslation(["common", "account"]);
  const { onLogin } = useAuth();

  const initialUser = {
    email: "",
    password: "",
  };

  const [user, setUser] = useState(initialUser);

  const handleUserChange = (key, value) =>
    setUser((prev) => ({ ...prev, [key]: value }));

  const formErrorKey = "undefined";
  const initialErrors = Object.keys(initialUser).reduce(
    (prev, curr) => ({ ...prev, [curr]: [] }),
    { [formErrorKey]: [] },
  );

  const [errors, setErrors] = useState(initialErrors);

  const clearErrors = () => {
    setErrors(initialErrors);
  };

  const handleLogin = async () => {
    clearErrors();
    const response = await onLogin(user);
    const status = response.status;

    switch (status) {
      case 200:
        // Redirection is done in the (public) route wrapper after the auth provider updates login
        // state when it receives the `onLogin` dispatch
        break;
      case 400: {
        const jsonResponse = await response.json();
        setErrors(mapAuthErrors(jsonResponse.errors));
        break;
      }
      case 401:
        onStatusChange("unverified");
        break;
      case 403:
        onStatusChange("inactive");
        break;
      default:
        console.log(response);
        break;
    }
  };

  const emailInput = {
    label: t("account:login.email"),
    name: "email",
    type: "email",
    autoComplete: "email",
    value: user.email,
    onChange: (value) => handleUserChange("email", value),
    errors: errors.email,
  };

  const passwordInput = {
    label: t("account:login.password"),
    name: "password",
    type: "password",
    autoComplete: "current-password",
    value: user.password,
    onChange: (value) => handleUserChange("password", value),
    errors: errors.password,
  };

  const inputs = [
    {
      data: [emailInput, passwordInput],
    },
  ];

  return (
    <Main>
      <Header>
        <Heading>
          <Trans
            t={t}
            i18nKey="account:login.active.heading"
            components={{
              emphasis: <span />,
            }}
          />
        </Heading>
        <SubHeading>
          <span>
            <Trans
              t={t}
              i18nKey="account:login.active.subheading"
              components={{
                anchor: <Link to={routes.Account.SignUp} />,
              }}
            />
          </span>
        </SubHeading>
      </Header>
      <Content>
        <Form
          inputs={inputs}
          errors={errors[formErrorKey]}
          button={t("account:login.button")}
          onSubmit={handleLogin}
        />
        <div className="Main-link" style={{ marginTop: "1rem" }}>
          <Link to={routes.Account.PasswordReset}>
            {t("account:login.active.forgotPassword")}
          </Link>
        </div>
      </Content>
    </Main>
  );
}

function Inactive() {
  const { t } = useTranslation(["common", "account"]);

  return (
    <Main id={"Inactive"}>
      <Header>
        <Heading>
          <Trans
            t={t}
            i18nKey="account:login.inactive.heading"
            components={{
              emphasis: <span />,
            }}
          />
        </Heading>
        <SubHeading>
          <span>
            <Trans
              t={t}
              i18nKey="account:login.inactive.subheading"
              components={{
                anchor: <Link to={`mailto:${routes.EmailSupport}`} />,
              }}
            />
          </span>
        </SubHeading>
      </Header>
    </Main>
  );
}

function Unverified() {
  const { t } = useTranslation(["common", "account"]);

  return (
    <Main>
      <Header>
        <Heading>
          <Trans
            t={t}
            i18nKey="account:login.unverified.heading"
            components={{
              emphasis: <span />,
            }}
          />
        </Heading>
        <SubHeading>
          <span>
            <Trans
              t={t}
              i18nKey="account:login.unverified.subheading"
              components={{
                anchor: <Link to={`mailto:${routes.EmailSupport}`} />,
              }}
            />
          </span>
        </SubHeading>
      </Header>
    </Main>
  );
}
